import type { ReactNode } from 'react'
import { ChevronRight, ListFilter } from 'lucide-react'
import { Badge } from '@components/ui/badge'
import { cn } from '@/utils'
import { useControllableValue } from '../hooks/use-controllable-value'

type TrayFiltersSectionProps = {
  /** Number of non-default filters — shown (with "Clear filters") on the header row when > 0. */
  activeCount: number
  /** Resets every filter inside this section. Only rendered while `activeCount` is > 0. */
  onClear: () => void
  /** Controlled expanded state; omit (with `onOpenChange`) to let the section own it. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Initial expanded state when uncontrolled. */
  defaultOpen?: boolean
  /** Filter controls, rendered under the header row while expanded. */
  children: ReactNode
}

/**
 * Collapsible "Filters" section shared by the right-panel trays. Matches the Figma
 * re-annotation ClikArt's own filters row (`clikart-panel/components/clikart-filters-row.tsx`)
 * already implements (2026-09-22, frame 277:23099): the active-count badge and "Clear filters" both hide entirely when no filter is
 * active, and both live IN the header row itself (collapsed or expanded) once one is — not
 * buried inside the expanded body.
 *
 * The row is a `role="button"` div, not a real `<button>`, because it contains a real nested
 * "Clear filters" `<button>` and a button cannot contain interactive content (same reasoning as
 * `photo-tray-thumbnail.tsx`'s click-vs-drag div); "Clear filters" stops propagation so clicking
 * it doesn't also toggle expand/collapse.
 */
export function TrayFiltersSection({
  activeCount,
  onClear,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  children,
}: TrayFiltersSectionProps) {
  const [open, setOpen] = useControllableValue(openProp, onOpenChange, defaultOpen)

  return (
    <div className="flex w-full min-w-0 flex-col">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          // Only the row's own keys toggle. Enter/Space on the nested "Clear filters" button
          // bubbles here too, and preventDefaulting it would cancel the button's native click
          // and collapse the section instead of clearing.
          if (event.target !== event.currentTarget) return
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          setOpen(!open)
        }}
        className="flex h-8 w-full min-w-0 cursor-pointer items-center gap-2 rounded-md p-2 outline-none hover:bg-accent/30 focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <ListFilter className="size-4 shrink-0 text-foreground" aria-hidden="true" />
        <span className="text-sm font-medium leading-none text-foreground">Filters</span>
        {activeCount > 0 ? (
          <>
            <Badge
              variant="outline"
              shape="pill"
              size="xs"
              aria-label={`${activeCount} active filters`}
              className="size-5 min-w-5 justify-center px-1 font-semibold"
            >
              {activeCount}
            </Badge>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onClear()
              }}
              className="text-xs font-normal leading-none text-muted-foreground underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              Clear filters
            </button>
          </>
        ) : null}
        <ChevronRight
          className={cn(
            'ml-auto size-4 shrink-0 text-foreground transition-transform',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        />
      </div>

      {open ? (
        <div className="flex w-full min-w-0 flex-col py-2.5 [&_[role=combobox]]:mb-0 [&_[role=combobox]]:w-full">
          {children}
        </div>
      ) : null}
    </div>
  )
}
